/**
 * Enforces MFA on protected routes.
 *
 * - Admins MUST have a verified authenticator. If they don't, they're forced
 *   to set one up before continuing (Cyber Essentials: MFA on admin accounts).
 * - Any user (admin or client) who has a verified authenticator but whose
 *   session is only at aal1 is prompted to enter a code (elevate to aal2).
 * - Clients without MFA are allowed through (MFA is optional for them).
 */
import { useCallback, useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getAal, listTotpFactors } from "@/lib/auth/mfa";
import HeroWave from "@/components/HeroWave";
import MfaEnroll from "./MfaEnroll";
import MfaChallenge from "./MfaChallenge";

const GRAD = "#5048E5";
const fg = "hsl(var(--foreground))";
const fgMuted = "hsl(var(--muted-foreground))";
const border = "hsl(var(--border))";
const surface = "hsl(var(--card))";

type Stage = "checking" | "enroll" | "challenge" | "ok";

function BlockingShell({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-6 py-12" style={{ background: "hsl(var(--background))" }}>
      <HeroWave fadeBottom={58} bleed="both" />
      <div className="relative w-full max-w-md rounded-2xl p-7" style={{ background: surface, border: `1px solid ${border}` }}>
        <div className="mb-5 flex flex-col items-center text-center">
          <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: `linear-gradient(135deg, ${GRAD}, #a855f7)`, color: "#fff" }}>
            <ShieldCheck size={22} />
          </span>
          <h1 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif", color: fg }}>{title}</h1>
          <p className="mt-1 text-sm" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{subtitle}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

// Once the gate is satisfied in this page session we skip re-checking on every
// sub-route navigation. Cleared automatically on full reload / sign-out.
let gateSatisfied = false;

/** Reset the cached gate decision (call on sign-in / sign-out). */
export function resetMfaGate() {
  gateSatisfied = false;
}

export default function MfaGate({ isAdmin, children }: { isAdmin: boolean; children: React.ReactNode }) {
  const [stage, setStage] = useState<Stage>(gateSatisfied ? "ok" : "checking");

  const evaluate = useCallback(async () => {
    try {
      const aal = await getAal();
      if (aal.currentLevel === "aal2") {
        gateSatisfied = true;
        setStage("ok");
        return;
      }
      const factors = await listTotpFactors();
      const hasVerified = factors.some((f) => f.status === "verified");
      if (hasVerified) {
        setStage("challenge");
      } else if (isAdmin) {
        setStage("enroll");
      } else {
        gateSatisfied = true;
        setStage("ok");
      }
    } catch {
      // If the MFA check fails, don't lock users out of optional flows; only
      // admins are hard-gated, so re-checking is safe to retry on next mount.
      setStage("ok");
    }
  }, [isAdmin]);

  useEffect(() => {
    if (gateSatisfied) return;
    evaluate();
  }, [evaluate]);

  function markSatisfied() {
    gateSatisfied = true;
    setStage("ok");
  }

  async function handleSignOut() {
    gateSatisfied = false;
    await supabase.auth.signOut();
    window.location.href = "/login";
  }

  if (stage === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ background: "hsl(var(--background))" }}>
        <div className="h-8 w-8 animate-spin rounded-full border-b-2" style={{ borderColor: GRAD }} />
      </div>
    );
  }

  if (stage === "enroll") {
    return (
      <BlockingShell
        title="Set up two-factor authentication"
        subtitle="Admin accounts must use an authenticator app before accessing the portal."
      >
        <MfaEnroll onVerified={markSatisfied} />
        <button type="button" onClick={handleSignOut}
          className="mt-3 w-full rounded-xl py-2.5 text-sm font-semibold transition-all hover:opacity-90"
          style={{ background: "hsl(var(--muted))", border: `1px solid ${border}`, color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
          Sign out
        </button>
      </BlockingShell>
    );
  }

  if (stage === "challenge") {
    return (
      <BlockingShell
        title="Two-factor verification"
        subtitle="Enter the code from your authenticator app to continue."
      >
        <MfaChallenge onVerified={markSatisfied} onSignOut={handleSignOut} />
      </BlockingShell>
    );
  }

  return <>{children}</>;
}
